import { ComponentProps, ReactNode, useState } from 'react'
import { RefreshCw, TriangleAlert } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import AssignmentBoxConfigurator from './AssignmentBoxConfigurator'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import AssignmentEntityChip from './AssignmentEntityChip'
import { AssignmentViewMode } from './assignmentTimeline'
import Card from 'app/frontend/base/components/Card'
import RaidstratsContainer, { RaidstratsPlayer } from './RaidstratsContainer'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
}

type Props = {
  scope: Scope
  castTimestamps: string[]
  entitiesByGroup: RaidPlanAssignmentEntity[][]
  assignmentGroups: number
  canEdit: boolean
  paletteEntities: ComponentProps<
    typeof AssignmentBoxConfigurator
  >['paletteEntities']
  assignmentUsageCountByIdentity: Record<string, number>
  onSave: (entitiesByGroup: RaidPlanAssignmentEntity[][]) => void
  assignLabel: string
  raidPlanId: string | null
  raidPlanPlayers: RaidstratsPlayer[]
  abilityViewMode: AssignmentViewMode
  duplicateIdentities: Set<string>
  getEntityLabel: (entity: RaidPlanAssignmentEntity) => ReactNode
  groupLabel: (count: number) => string
}

const TeamPlanAssignmentScopeCard = ({
  scope,
  castTimestamps,
  entitiesByGroup,
  assignmentGroups,
  canEdit,
  paletteEntities,
  assignmentUsageCountByIdentity,
  onSave,
  assignLabel,
  raidPlanId,
  raidPlanPlayers,
  abilityViewMode,
  duplicateIdentities,
  getEntityLabel,
  groupLabel,
}: Props) => {
  const [showRaidPlan, setShowRaidPlan] = useState(false)
  const [raidPlanReloadKey, setRaidPlanReloadKey] = useState(0)
  return (
    <Card>
      <div className='mb-2 flex flex-wrap items-center gap-4'>
        <div className='font-medium text-sm'>{scope.label}</div>
        <div className='flex flex-wrap grow items-center gap-1 text-xs text-muted-foreground'>
          {scope.castIndexes.map((castIndex, index) => (
            <Badge key={castIndex} variant='outline'>
              {castTimestamps[index]}
            </Badge>
          ))}
        </div>
        <div className='flex items-center gap-2'>
          {raidPlanId ? (
            <>
              {showRaidPlan ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() => {
                        setShowRaidPlan(true)
                        setRaidPlanReloadKey((value) => value + 1)
                      }}
                    >
                      <RefreshCw className='h-3.5 w-3.5' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Fetch latest raid plan version
                  </TooltipContent>
                </Tooltip>
              ) : null}
              <Button
                type='button'
                variant='outline'
                size='custom'
                className='h-7 py-0.5 px-2'
                onClick={() => setShowRaidPlan((value) => !value)}
              >
                {showRaidPlan ? 'Hide raid plan' : 'View raid plan'}
              </Button>
            </>
          ) : null}
          {canEdit ? (
            <AssignmentBoxConfigurator
              entitiesByGroup={entitiesByGroup}
              paletteEntities={paletteEntities}
              assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
              canEdit={canEdit}
              onSave={onSave}
              trigger={
                <Button
                  type='button'
                  variant='outline'
                  size='custom'
                  className='h-7 py-0.5 px-2'
                >
                  {assignLabel}
                </Button>
              }
            />
          ) : null}
        </div>
      </div>

      {showRaidPlan && raidPlanId ? (
        <div className='mb-3'>
          <RaidstratsContainer
            key={`${scope.id}-${raidPlanReloadKey}`}
            raidPlanId={raidPlanId}
            players={raidPlanPlayers}
          />
        </div>
      ) : null}

      <div
        className='grid gap-2'
        style={{
          gridTemplateColumns: `repeat(${Math.max(
            Math.min(assignmentGroups, 4),
            1,
          )}, minmax(0, 1fr))`,
        }}
      >
        {entitiesByGroup.map((groupEntities, boxIndex) => (
          <div key={boxIndex} className='mb-1'>
            {assignmentGroups > 1 ? (
              <div className='mb-1 pb-1'>
                <div className='text-xs'>{groupLabel(boxIndex + 1)}</div>
              </div>
            ) : null}
            {groupEntities.length === 0 ? (
              <div className='text-xs text-muted-foreground text-italic'>
                No entities assigned yet
              </div>
            ) : (
              <div className='flex flex-wrap items-center gap-1'>
                {groupEntities.map((entity, entityIndex) => {
                  const entityIdentity = getAssignmentEntityIdentity(entity)
                  const showDuplicateWarning =
                    entity.kind !== 'by_role' &&
                    abilityViewMode !== 'all_separate' &&
                    duplicateIdentities.has(entityIdentity)
                  return (
                    <div
                      key={`${entity.kind}-${entityIndex}-${entity.kind}`}
                      className='inline-flex items-center'
                    >
                      <AssignmentEntityChip
                        index={entityIndex}
                        label={getEntityLabel(entity)}
                        warning={showDuplicateWarning}
                        endSlot={
                          showDuplicateWarning ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='inline-flex items-center text-warning-foreground'>
                                  <TriangleAlert className='h-3.5 w-3.5' />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                This entity is in multiple groups
                              </TooltipContent>
                            </Tooltip>
                          ) : undefined
                        }
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

export default TeamPlanAssignmentScopeCard
